// Cave84 — Wine Analysis service (Phase 2 Week 7)
// WineAnalysis is stored as a subdocument field `analysis` on the Wine doc.
// Writing uses updateDoc on the wine document; reading uses the existing
// Wine document (analysis is included in useWine's response).
//
// Pages/components MUST NOT import here directly — use hooks/useWineAnalysis.ts.

import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import type { AnalysisStatus, GenerationQueueEntry, Wine, WineAnalysis } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection } from './firestore';

const WINES_COLLECTION = 'wines';
const QUEUE_COLLECTION = 'generation_queue';

// ---------------------------------------------------------------------------
// Read — analysis lives on the Wine doc (already fetched by useWine)
// ---------------------------------------------------------------------------

/** Extract the analysis from a Wine doc, or return null if not yet generated. */
export function extractAnalysis(wine: Wine): WineAnalysis | null {
  return wine.analysis ?? null;
}

// ---------------------------------------------------------------------------
// Queue for regeneration (creates a generation_queue entry)
// ---------------------------------------------------------------------------

export type QueuePriority = 'high' | 'normal' | 'low';

export async function queueWineAnalysis(
  wineId: string,
  priority: QueuePriority = 'normal'
): Promise<string> {
  const ref = doc(collection(db, QUEUE_COLLECTION));
  const payload: Omit<GenerationQueueEntry, 'id'> = {
    entity_type: 'wine_analysis',
    entity_id: wineId,
    priority,
    status: 'pending',
    requested_at: serverTimestamp() as GenerationQueueEntry['requested_at'],
  };
  await setDoc(ref, payload);
  return ref.id;
}

// ---------------------------------------------------------------------------
// Update analysis status (called after manual edit or regeneration)
// ---------------------------------------------------------------------------

export async function updateAnalysisStatus(
  wineId: string,
  status: AnalysisStatus
): Promise<void> {
  await updateDoc(doc(db, WINES_COLLECTION, wineId), {
    'analysis.status': status,
    'analysis.manually_edited': status === 'manually_edited',
  });
}

// ---------------------------------------------------------------------------
// Save manual owner override to a specific section.
// Writes to BOTH the live section (for immediate display) AND owner_overrides
// (so the CLI batch job can skip this section on future regenerations).
// Spec §1546: "Edit stored in owner_overrides[section_name]"
// Spec §1550: "If owner_overrides exists for a section → Claude Code skips it"
// ---------------------------------------------------------------------------

export async function saveOwnerOverride(
  wineId: string,
  sectionKey: keyof Pick<WineAnalysis, 'producer_note' | 'terroir_note'>,
  content: string
): Promise<void> {
  await updateDoc(doc(db, WINES_COLLECTION, wineId), {
    // Live field — shown immediately in UI
    [`analysis.${sectionKey}`]: content,
    // owner_overrides — persisted across regenerations (CLI reads this)
    [`analysis.owner_overrides.${sectionKey}`]: content,
    // Status flags
    'analysis.status': 'manually_edited' as AnalysisStatus,
    'analysis.manually_edited': true,
  });
}

// ---------------------------------------------------------------------------
// Clear all owner overrides (Regenerate from scratch).
// Spec §1551: "owner_overrides cleared, full regeneration runs"
// Queues the wine for full regeneration after clearing.
// ---------------------------------------------------------------------------

export async function clearOwnerOverridesAndQueue(
  wineId: string,
  priority: QueuePriority = 'high'
): Promise<string> {
  // Clear overrides & reset manually_edited flag
  await updateDoc(doc(db, WINES_COLLECTION, wineId), {
    'analysis.owner_overrides': null,
    'analysis.manually_edited': false,
    'analysis.status': 'pending' as AnalysisStatus,
  });

  // Queue for full regeneration
  const ref = doc(collection(db, QUEUE_COLLECTION));
  const payload: Omit<GenerationQueueEntry, 'id'> = {
    entity_type: 'wine_analysis',
    entity_id: wineId,
    priority,
    status: 'pending',
    requested_at: serverTimestamp() as GenerationQueueEntry['requested_at'],
  };
  await setDoc(ref, payload);
  return ref.id;
}

// ---------------------------------------------------------------------------
// Count wines with no analysis (analysis field is null / missing)
// ---------------------------------------------------------------------------

export async function countWinesWithoutAnalysis(): Promise<number> {
  const snap = await getDocs(
    query(
      collection(db, WINES_COLLECTION),
      where('analysis', '==', null)
    )
  );
  return snap.size;
}

// ---------------------------------------------------------------------------
// List pending wine analysis queue entries
// ---------------------------------------------------------------------------

export async function listPendingWineAnalysisJobs(): Promise<GenerationQueueEntry[]> {
  const ref = typedCollection<GenerationQueueEntry>(QUEUE_COLLECTION);
  const snap = await getDocs(
    query(
      ref,
      where('entity_type', '==', 'wine_analysis'),
      where('status', 'in', ['pending', 'processing']),
      orderBy('requested_at', 'asc')
    )
  );
  return snap.docs.map(d => d.data());
}
